import React, { useEffect, useRef, useState } from 'react';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  REVIEW_TYPE_OF_PROVIDER,
  REVIEW_TYPE_OF_CUSTOMER,
  SCHEMA_TYPE_MULTI_ENUM,
  propTypes,
} from '../../util/types';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../util/errors';
import { pickCustomFieldProps } from '../../util/fieldHelpers';
import {
  getCurrentUserTypeRoles,
  hasPermissionToViewData,
  isUserAuthorized,
} from '../../util/userHelpers';
import { richText } from '../../util/richText';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Heading,
  H2,
  H4,
  Page,
  AvatarLarge,
  ExternalLink,
  NamedLink,
  ListingCard,
  DesignListingCard,
  PaginationLinks,
  Reviews,
  ButtonTabNavHorizontal,
  LayoutSingleColumn,
  NamedRedirect,
  FoundingBadge,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import { queryUserListings, queryUserListingsByIds } from './ProfilePage.duck';

import css from './ProfilePage.module.css';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';

const MAX_MOBILE_SCREEN_WIDTH = 768;
const MIN_LENGTH_FOR_LONG_WORDS = 20;
// Meta description budget. Search engines cut the snippet around 155-160
// characters and most link-preview cards show even less, so a longer
// string only buys a mid-word "…" in someone else's UI.
const MAX_META_DESCRIPTION_LENGTH = 160;

// Matterax: AsideContent removed (May 2026). Avatar / name / bio /
// edit link all live in MainContent's centered hero block now, and
// LayoutSideNavigation was swapped for LayoutSingleColumn so the
// vertical divider between sidebar and main no longer renders.

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const MobileReviews = props => {
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  return (
    <div className={css.mobileReviews}>
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsFromMyCustomersTitle"
          values={{ count: reviewsOfProvider.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfProvider} />
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsAsACustomerTitle"
          values={{ count: reviewsOfCustomer.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfCustomer} />
    </div>
  );
};

export const DesktopReviews = props => {
  const { reviews, queryReviewsError, userTypeRoles } = props;
  const { customer: isCustomerUserType, provider: isProviderUserType } = userTypeRoles;

  const initialReviewState = !isProviderUserType
    ? REVIEW_TYPE_OF_CUSTOMER
    : REVIEW_TYPE_OF_PROVIDER;
  const [showReviewsType, setShowReviewsType] = useState(initialReviewState);

  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  const isReviewTypeProviderSelected = showReviewsType === REVIEW_TYPE_OF_PROVIDER;
  const isReviewTypeCustomerSelected = showReviewsType === REVIEW_TYPE_OF_CUSTOMER;
  const providerReviewsMaybe = isProviderUserType
    ? [
        {
          text: (
            <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
              <FormattedMessage
                id="ProfilePage.reviewsFromMyCustomersTitle"
                values={{ count: reviewsOfProvider.length }}
              />
            </Heading>
          ),
          selected: isReviewTypeProviderSelected,
          onClick: () => setShowReviewsType(REVIEW_TYPE_OF_PROVIDER),
        },
      ]
    : [];

  const customerReviewsMaybe = isCustomerUserType
    ? [
        {
          text: (
            <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
              <FormattedMessage
                id="ProfilePage.reviewsAsACustomerTitle"
                values={{ count: reviewsOfCustomer.length }}
              />
            </Heading>
          ),
          selected: isReviewTypeCustomerSelected,
          onClick: () => setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER),
        },
      ]
    : [];
  const desktopReviewTabs = [...providerReviewsMaybe, ...customerReviewsMaybe];

  return (
    <div className={css.desktopReviews}>
      <div className={css.desktopReviewsWrapper}>
        <ButtonTabNavHorizontal className={css.desktopReviewsTabNav} tabs={desktopReviewTabs} />

        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />

        {isReviewTypeProviderSelected ? (
          <Reviews reviews={reviewsOfProvider} />
        ) : (
          <Reviews reviews={reviewsOfCustomer} />
        )}
      </div>
    </div>
  );
};

export const CustomUserFields = props => {
  const { publicData, metadata, userFieldConfig, onChipClick, selectedChipKey } = props;

  const shouldPickUserField = fieldConfig => fieldConfig?.showConfig?.displayInProfile !== false;
  const propsForCustomFields =
    pickCustomFieldProps(publicData, metadata, userFieldConfig, 'userType', shouldPickUserField) ||
    [];

  // Matterax (Aug 2026): the profile page is deliberately minimal — cover
  // photo, avatar, name, bio, social links, then the products. The
  // "Details" table (Based in), the free-text sections (Company address)
  // and the embedded-video section used to render between the hero and
  // the catalogue, pushing the products below the fold behind data a
  // designer never came here for. They are dropped rather than
  // un-ticked in Console because the same field configs still drive
  // /profile-settings, where suppliers keep filling them in.
  //
  // The multi-enum chip cluster is the one survivor: on a brand-partner
  // profile it is the in-place brand filter for the listings below, not
  // a profile "detail".
  return (
    <>
      {propsForCustomFields.map(customFieldProps => {
        const { schemaType, key, ...fieldProps } = customFieldProps;
        return schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
          <SectionMultiEnumMaybe
            key={key}
            {...fieldProps}
            // Matterax: when the host page (ProfilePage) wires up an
            // onChipClick callback, the chip cluster becomes a local
            // filter toggle instead of a Link to /s. The brand chips on
            // a brand-partner profile use this to filter the listings
            // shown beneath, without redirecting the user out.
            onChipClick={onChipClick}
            selectedChipKey={selectedChipKey}
          />
        ) : null;
      })}
    </>
  );
};

export const MainContent = props => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    user,
    isOwn,
    userShowError,
    bio,
    displayName,
    listings,
    pagination,
    collectionIndex,
    config: profileConfig,
    queryListingsError,
    reviews = [],
    queryReviewsError,
    publicData,
    metadata,
    userFieldConfig,
    intl,
    hideReviews,
    userTypeRoles,
    location,
  } = props;
  const dispatch = useDispatch();

  // Matterax: brand chip click filters the listings shown below, in-place,
  // without redirecting to /s. State lives here so the chip cluster
  // (rendered inside CustomUserFields → SectionMultiEnumMaybe) and the
  // listings grid stay in sync. Click the same chip again to clear.
  const [selectedBrandSlug, setSelectedBrandSlug] = useState(null);
  const handleBrandChipClick = key => {
    setSelectedBrandSlug(prev => (prev === key ? null : key));
  };

  // Two-tier collection filter (May 2026): big-catalogue suppliers
  // (Suzuka 62 collections, Cosentino dozens) can't fit one flat pill
  // row without it wrapping into a wall of 60+ chips. Group by product
  // family first ("STRATO", "KASTONE Brick", "ARCHISTRA", "Textured
  // Coatings", "Stone Veneer", etc.). Pick a family → second row
  // reveals that family's sub-collections.
  //
  // selectedFamily and selectedCollection are independent useStates so
  // a designer can browse "STRATO" family without committing to one
  // sub-collection.
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);

  // Unclaimed-profile notice: collapsed to the chip alone; the explanation +
  // claim/remove actions reveal on hover (desktop, CSS) or tap (this state).
  const [unclaimedOpen, setUnclaimedOpen] = useState(false);

  // Back button — uses browser history when there's something to go back to,
  // otherwise sends the user to the search catalogue (most common entry point
  // to a brand-partner profile is a click on a listing card → ListingPage →
  // author profile, so /s is the right "home" fallback). useHistory hook so
  // we don't have to thread history through the props chain.
  const history = useHistory();
  const handleBack = () => {
    if (history.length > 1) {
      history.goBack();
    } else {
      history.push('/s');
    }
  };

  // The hide-from-search filter still applies to the loaded page so a
  // half-loaded index doesn't briefly leak hidden listings; everything
  // else (pill counts, pill filtering) goes through the lightweight
  // catalogue index now.
  const visibleListings = isOwn
    ? listings
    : (listings || []).filter(l => l?.attributes?.publicData?.hidden_from_search !== true);

  // Two-tier pills derive counts from the FULL catalogue index, not the
  // current page. Without this, a 153-listing brand paginated 50/page
  // shows pill counts that only reflect the first page (a "Barroco (1)"
  // chip that really has 50 listings). The index also drives pill-click
  // filtering: clicking a pill collects all matching ids from the index
  // and re-fetches that filtered set via queryUserListingsByIds (capped
  // at 100). `indexScoped` is the brand-slug-narrowed index used to
  // derive both pill counts and the id sets sent on click.
  const idx = collectionIndex || [];
  const indexFiltered = isOwn ? idx : idx.filter(e => !e.hiddenFromSearch);
  const indexScoped = selectedBrandSlug
    ? indexFiltered.filter(e => (e.brandSlug || null) === selectedBrandSlug)
    : indexFiltered;

  // Family map matches the supplier's own catalogues page exactly:
  //   https://www.suzuka.com.my/catalogs/
  // Each family corresponds to one or more catalogue PDFs the supplier
  // publishes. Designers familiar with Suzuka's catalogue will recognise
  // these family names verbatim.
  //
  // Some catalogue PDFs bundle multiple sub-collections (e.g. "STRATO
  // Cement Texture & Velours" → both Cement Texture AND Velours roll up
  // into the STRATO family). The sub-pill row drills into the
  // individual collections within that catalogue.
  //
  // Rules ordered most-specific → most-general; first match wins.
  const familyFor = name => {
    if (!name) return 'Other';

    // Cosentino — collections are line-prefixed ("Dekton Onirika",
    // "Silestone Ethereal", "Scalea", "Sensa Premium"). Group tier-1 by the
    // product line so the brand page reads Cosentino → Dekton/Silestone/
    // Scalea/Sensa → series (the Material Bank / Architonic model).
    const cosLine = name.match(/^(Dekton|Silestone|Scalea|Sensa)\b/);
    if (cosLine) return cosLine[1];

    // Lam Chuan — one account = one brand; its sub-brands (WE Laminates,
    // AVO Compact) are line-prefixed collections. Group tier-1 by sub-brand.
    const lamLine = name.match(/^(WE Laminates|AVO Compact)\b/);
    if (lamLine) return lamLine[1];

    // STRATO® Interior Texture Paint — 11 sub-collections, 5 catalogue PDFs
    if (/^STRATO®/.test(name)) return 'STRATO® Interior Texture Paint';

    // KASTONE® Brick Veneer — one umbrella catalogue covers all
    // sub-series. BK-NN + DS-10/50/90 (DS-10/50/90 live under Brick
    // Collection per the supplier site; other DS-NN live under Stone).
    if (/^(BK-\d|BL-61|BQ-00|AR-30|DS-(10|50|90) Series)/i.test(name))
      return 'KASTONE® Brick Veneer';

    // KASTONE® Edge — its own catalogue
    if (/^KASTONE® EDGE/i.test(name)) return 'KASTONE® Edge';

    // KASTONE® Stone Veneer — one umbrella catalogue. All non-brick
    // DS-NN + KST/PS/WS sub-series.
    if (
      /^(DS-(00|02|03|04|05|06|07|08|09|19|30|40|60|80|70|11|20)|KST-\d|PS-\d|WS[-\s]\d)/i.test(
        name
      )
    )
      return 'KASTONE® Stone Veneer';

    // ARCHISTRA® Wall Panel — 6 sub-series, one catalogue
    if (/^(ARCHISTRA®|ACH-\d)/.test(name)) return 'ARCHISTRA® Wall Panel';

    // Standalone product lines — each has its own catalogue PDF
    if (/^(Concrete Effect Wall Panel|KASTPANEL)/i.test(name))
      return 'KASTPANEL® Concrete Wall Panel';
    if (/^(MONOKRETE|Exterior Textured Paint)/i.test(name))
      return 'MONOKRETE® Exterior Textured Paint';
    if (/^(TERRAVA|Microcement)/i.test(name)) return 'TERRAVA® Microcement';
    if (/^(LINOSA|Formulated Lime)/i.test(name)) return 'LINOSA® Lime Plaster';
    if (/^Stoneweave/i.test(name)) return 'Stoneweave®';
    if (/^(ECES|Concrete Effect Coating)/i.test(name)) return 'ECES® Exposed Concrete';
    if (/^(SGM|Grani[ -]?Multicolor)/i.test(name)) return 'SUZUKA® Grani Multicolor';
    if (/Modena/i.test(name)) return 'SUZUKA® Modena Coating';
    if (/^(Kreterra|Cement Texture Paint \(Floor)/i.test(name)) return 'Kreterra Cement Texture';
    if (/Jumbo Spray/i.test(name)) return 'SUZUKA® Jumbo Spray Tile';

    // SUZUKA® Texture Coating — umbrella catalogue covering Ceramic
    // Coat, Stony Coat, MGM Granite Effect, SGC Granilyte
    if (/^(Ceramic Coat|Stony Coat|Granite Effect|SGC|MGM)/i.test(name))
      return 'SUZUKA® Texture Coating';

    return name; // fallback — surface unknowns directly
  };

  // Effective collection label per listing. Most brands set
  // publicData.collection. Stone suppliers (Marble Emporium, Stone Empire,
  // Caesarstone, Neolith) don't — they organise their catalogue by
  // publicData.stone_family (material type: marble, granite, onyx,
  // engineered-quartz, sintered-ultra-compact…). So when a brand has NO
  // collection data at all, fall back to a prettified stone_family — that
  // gives those brands the same two-tier pills as Suzuka. Brands that DO
  // use collections (the check is brand-scoped) keep using them, so a
  // stray collection-less listing won't pollute their pill row.
  const prettyStoneFamily = sf =>
    sf
      .split('-')
      .map(w => (w ? w[0].toUpperCase() + w.slice(1) : w))
      .join(' ');
  // Stone suppliers (Marble Emporium, Stone Empire…) don't set
  // publicData.collection — they use publicData.stone_family. Fall back
  // to a prettified stone_family for those brands so they still get
  // pills. Checked at the index level, not the loaded-page level.
  const brandUsesCollection = indexScoped.some(e => e.collection);
  const labelFor = e => {
    if (brandUsesCollection) return e.collection;
    return e.stoneFamily ? prettyStoneFamily(e.stoneFamily) : '';
  };

  // 1st-tier counts: families. Each family's count is the sum of its
  // collections' listings within indexScoped. Same shape as the old map
  // but built from the full catalogue index.
  const familyCounts = new Map();
  // family → Map(collection → count)
  const subCollectionsByFamily = new Map();
  // family → ids[] (sorted by entry order in the index, which preserves
  // the brand's own ordering). Used to pull listings for the active
  // pill via queryUserListingsByIds (ids= filter, capped at 100).
  const familyIds = new Map();
  // family → collection → ids[]
  const subCollectionIds = new Map();
  for (const e of indexScoped) {
    const collection = labelFor(e);
    if (!collection) continue;
    const family = familyFor(collection);
    familyCounts.set(family, (familyCounts.get(family) || 0) + 1);
    if (!subCollectionsByFamily.has(family)) subCollectionsByFamily.set(family, new Map());
    subCollectionsByFamily
      .get(family)
      .set(collection, (subCollectionsByFamily.get(family).get(collection) || 0) + 1);
    if (!familyIds.has(family)) familyIds.set(family, []);
    familyIds.get(family).push(e.id);
    if (!subCollectionIds.has(family)) subCollectionIds.set(family, new Map());
    const subs = subCollectionIds.get(family);
    if (!subs.has(collection)) subs.set(collection, []);
    subs.get(collection).push(e.id);
  }

  // Resolved sub-collections row: only shown when a family is selected
  // AND that family has >1 sub-collection.
  const activeSubCollections = selectedFamily
    ? subCollectionsByFamily.get(selectedFamily) || new Map()
    : new Map();

  // When a pill is active, the listings array IS the filtered result
  // (we re-fetch it via queryUserListingsByIds below). When no pill is
  // active, `listings` is just the current pagination page. Either way
  // the grid shows whatever's loaded.
  const hasAnyFilter = !!selectedBrandSlug || !!selectedFamily || !!selectedCollection;
  // hide hidden system-asset anchors (e.g. the profile-cover holder listing)
  const filteredListings = visibleListings.filter(l => !l?.attributes?.publicData?.systemAsset);
  const hasListings = filteredListings.length > 0;

  // Filter total — what to display next to "X of Y". Pulled from the
  // index since the loaded page is at most 100.
  const filterIds = selectedCollection
    ? subCollectionIds.get(selectedFamily)?.get(selectedCollection) || []
    : selectedFamily
    ? familyIds.get(selectedFamily) || []
    : selectedBrandSlug
    ? indexScoped.map(e => e.id)
    : [];
  const filterTotal = filterIds.length;
  const filterTruncated = filterTotal > 100;

  // Dispatch chain when the active pill changes. On first mount the
  // initial page-N listings were already loaded by loadData, so we skip
  // the first run. After that, every pill change re-fetches: a filter
  // pulls matching ids from the index, no filter restores page-N.
  const initialDispatchSkippedRef = useRef(false);
  const currentPage = (() => {
    const search =
      location?.search || (typeof window !== 'undefined' ? window.location.search : '');
    const m = /[?&]page=(\d+)/.exec(search || '');
    return m ? parseInt(m[1], 10) || 1 : 1;
  })();
  useEffect(() => {
    if (!initialDispatchSkippedRef.current) {
      initialDispatchSkippedRef.current = true;
      return;
    }
    if (!user?.id?.uuid || !profileConfig) return;
    if (hasAnyFilter) {
      // Empty filter → empty grid (rather than a wider re-fetch). Most
      // pills land here only when the index hasn't loaded yet; the
      // effect re-runs once collectionIndex arrives.
      if (filterIds.length === 0) return;
      dispatch(queryUserListingsByIds(filterIds, profileConfig));
    } else {
      dispatch(queryUserListings(user.id.uuid, profileConfig, isOwn, currentPage));
    }
    // filterIds is recomputed every render, so depend on its
    // identity-bearing inputs (selectedFamily/selectedCollection/
    // selectedBrandSlug) plus the index existence flag. (No
    // exhaustive-deps suppression needed — that rule isn't part of
    // this project's eslint config, and referencing it by name in a
    // disable directive is itself a build-breaking lint error.)
  }, [selectedFamily, selectedCollection, selectedBrandSlug, collectionIndex?.length]);
  const hasMatchMedia = typeof window !== 'undefined' && window?.matchMedia;
  const isMobileLayout =
    mounted && hasMatchMedia
      ? window.matchMedia(`(max-width: ${MAX_MOBILE_SCREEN_WIDTH}px)`)?.matches
      : true;

  const hasBio = !!bio;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });

  // Brand bios run long (Guocera's is two full paragraphs), and an
  // unclamped one buries the social links and the whole catalogue under a
  // wall of text. The clamp itself lives in .heroBioClamped; this only
  // decides whether to offer "Read more", and only when the text actually
  // overflows, so short bios don't get a pointless toggle. Overflow is
  // measured, not guessed from character count: line height and column
  // width both change with viewport.
  const bioRef = useRef(null);
  const [bioExpanded, setBioExpanded] = useState(false);
  const [bioOverflows, setBioOverflows] = useState(false);
  useEffect(() => {
    // Only measure while collapsed — expanded, scrollHeight equals
    // clientHeight and we'd wrongly conclude the bio fits, yanking the
    // "Read less" button out from under the reader.
    if (bioExpanded) {
      return undefined;
    }
    const measure = () => {
      const el = bioRef.current;
      if (el) {
        setBioOverflows(el.scrollHeight - el.clientHeight > 1);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [bio, bioExpanded]);

  const listingsContainerClasses = classNames(css.listingsContainer, {
    [css.withBioMissingAbove]: !hasBio,
  });

  // Website + social links (brands and design pros set these in
  // /profile-settings). Values may be pasted without a scheme.
  const normalizeUrl = v =>
    typeof v === 'string' && v.trim()
      ? /^https?:\/\//i.test(v.trim())
        ? v.trim()
        : `https://${v.trim()}`
      : null;
  const profileLinks = [
    ['websiteUrl', 'Website'],
    ['socialInstagram', 'Instagram'],
    ['socialFacebook', 'Facebook'],
    ['socialTiktok', 'TikTok'],
    ['socialYoutube', 'YouTube'],
    ['socialThreads', 'Threads'],
    ['socialXhs', 'XHS'],
  ]
    .map(([key, label]) => ({ key, label, href: normalizeUrl(publicData?.[key]) }))
    .filter(l => l.href);
  const coverPhotoUrl = publicData?.coverPhoto?.url || null;
  // Design pros publish projects, not products: native userType from Console
  // signups, profileType for the designer accounts provisioned before user
  // types existed.
  const isDesignProProfile =
    publicData?.userType === 'design-pro' || publicData?.profileType === 'designer';

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }
  return (
    <div>
      <button type="button" className={css.backButton} onClick={handleBack}>
        <span aria-hidden="true">←</span> <FormattedMessage id="ProfilePage.backButton" />
      </button>
      {/* Desktop hero: avatar above the name, bio underneath. The aside
          still renders the avatar/bio on mobile (where AsideContent is
          stacked above MainContent by LayoutSideNavigation) — its desktop
          copies are hidden via CSS so we don't duplicate. The heading
          itself is just the brand name now (no "Hello, I'm …" greeting). */}
      {publicData?.unclaimedProfile === true ? (
        <div
          className={classNames(css.unclaimedNotice, {
            [css.unclaimedNoticeOpen]: unclaimedOpen,
          })}
        >
          <button
            type="button"
            className={css.unclaimedChip}
            onClick={() => setUnclaimedOpen(o => !o)}
            aria-expanded={unclaimedOpen}
          >
            <span className={css.unclaimedDot} aria-hidden="true" />
            <span className={css.unclaimedBadge}>
              <FormattedMessage id="ProfilePage.unclaimedBadge" />
            </span>
            <span className={css.unclaimedChevron} aria-hidden="true" />
          </button>
          <div className={css.unclaimedReveal}>
            <div className={css.unclaimedBody}>
              <p className={css.unclaimedText}>
                <FormattedMessage id="ProfilePage.unclaimedText" values={{ displayName }} />
              </p>
              <div className={css.unclaimedActions}>
                <ExternalLink
                  className={css.unclaimedClaimLink}
                  href={`mailto:my@matterax.com?subject=${encodeURIComponent(
                    `Claim profile: ${displayName || ''}`
                  )}`}
                >
                  <FormattedMessage id="ProfilePage.unclaimedClaimCta" />
                </ExternalLink>
                <span className={css.unclaimedActionDivider} aria-hidden="true">
                  ·
                </span>
                <ExternalLink
                  className={css.unclaimedRemoveLink}
                  href={`mailto:my@matterax.com?subject=${encodeURIComponent(
                    `Remove profile: ${displayName || ''}`
                  )}`}
                >
                  <FormattedMessage id="ProfilePage.unclaimedRemoveCta" />
                </ExternalLink>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {coverPhotoUrl ? (
        <div className={css.profileCover}>
          <img src={coverPhotoUrl} alt="" />
        </div>
      ) : null}
      <div className={classNames(css.profileHero, { [css.profileHeroWithCover]: !!coverPhotoUrl })}>
        <AvatarLarge className={css.heroAvatar} user={user} disableProfileLink />
        <H2 as="h1" className={css.desktopHeading}>
          {displayName}
          <FoundingBadge user={user} className={css.foundingBadgeHero} />
        </H2>
        {isOwn ? (
          <NamedLink className={css.heroEditLink} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
          </NamedLink>
        ) : null}
        {bioWithLinks ? (
          <>
            <p
              ref={bioRef}
              className={classNames(css.heroBio, { [css.heroBioClamped]: !bioExpanded })}
            >
              {bioWithLinks}
            </p>
            {bioOverflows ? (
              <button
                type="button"
                className={css.bioToggle}
                onClick={() => setBioExpanded(expanded => !expanded)}
                aria-expanded={bioExpanded}
              >
                <FormattedMessage
                  id={bioExpanded ? 'ProfilePage.bioReadLess' : 'ProfilePage.bioReadMore'}
                />
              </button>
            ) : null}
          </>
        ) : null}
        {profileLinks.length > 0 ? (
          <div className={css.profileLinks}>
            {profileLinks.map(l => (
              <ExternalLink key={l.key} className={css.profileLinkPill} href={l.href}>
                {l.label}
              </ExternalLink>
            ))}
          </div>
        ) : null}
      </div>

      {displayName ? (
        <CustomUserFields
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFieldConfig}
          intl={intl}
          onChipClick={handleBrandChipClick}
          selectedChipKey={selectedBrandSlug}
        />
      ) : null}

      {/* Two-tier collection filter. Tier 1: product families
          (STRATO, KASTONE Brick, ARCHISTRA, Textured Coatings, Stone
          Veneer, plus standalone product lines as singleton-families).
          Tier 2: sub-collections within the selected family, only
          rendered when there's >1 sub. Toggle-to-clear. */}
      {familyCounts.size > 1 ? (
        <div className={css.pillClusters}>
          <div className={css.pillRow}>
            <span className={css.pillRowLabel}>
              <FormattedMessage id="ProfilePage.filterByCollection" defaultMessage="Collection" />
            </span>
            <div className={css.pillRowScroll}>
              {[...familyCounts.entries()]
                .sort((a, b) => b[1] - a[1])
                .map(([family, count]) => {
                  const isActive = selectedFamily === family;
                  return (
                    <button
                      key={family}
                      type="button"
                      className={classNames(css.pill, {
                        [css.pillActive]: isActive,
                      })}
                      onClick={() => {
                        // Toggle family; clear sub-collection on switch
                        setSelectedFamily(prev => (prev === family ? null : family));
                        setSelectedCollection(null);
                      }}
                    >
                      {family}
                      <span className={css.pillCount}>{count}</span>
                    </button>
                  );
                })}
            </div>
          </div>
          {/* Sub-collection row, only when family is selected AND it
              has more than one child. Singleton families need no drill-down. */}
          {selectedFamily && activeSubCollections.size > 1 ? (
            <div className={classNames(css.pillRow, css.pillRowSub)}>
              <span className={css.pillRowLabel}>
                <FormattedMessage
                  id="ProfilePage.filterBySubCollection"
                  defaultMessage="In {family}"
                  values={{ family: selectedFamily }}
                />
              </span>
              <div className={css.pillRowScroll}>
                {[...activeSubCollections.entries()]
                  .sort((a, b) => b[1] - a[1])
                  .map(([name, count]) => {
                    const isActive = selectedCollection === name;
                    return (
                      <button
                        key={name}
                        type="button"
                        className={classNames(css.pill, css.pillSmall, {
                          [css.pillActive]: isActive,
                        })}
                        onClick={() => setSelectedCollection(prev => (prev === name ? null : name))}
                      >
                        {/* Drop the family prefix from display — it's
                            redundant with the row label above (the
                            "In STRATO" context already implies STRATO®). */}
                        {name
                          .replace(/^STRATO®\s*/, '')
                          .replace(/^KASTONE®\s*(Brick\s*)?/, '')
                          .replace(/^ARCHISTRA®\s*/, '')}
                        <span className={css.pillCount}>{count}</span>
                      </button>
                    );
                  })}
              </div>
            </div>
          ) : null}
        </div>
      ) : null}

      {hasListings ? (
        <div className={listingsContainerClasses}>
          <H4 as="h2" className={css.listingsTitle}>
            {hasAnyFilter ? (
              <FormattedMessage
                id="ProfilePage.listingsTitleFiltered"
                values={{
                  // "X of Y" — X is what's currently on screen (the
                  // filter result is fetched in full up to the 100-id
                  // cap), Y is the total matching the pill across the
                  // full catalogue.
                  count: filteredListings.length,
                  total: filterTotal,
                }}
              />
            ) : (
              <FormattedMessage
                // Design pros get "Projects" — a separate key (not the
                // Console-overridden listingsTitle, which reads "My products").
                id={isDesignProProfile ? 'ProfilePage.projectsTitle' : 'ProfilePage.listingsTitle'}
                // Profile fetch is now paginated; visibleListings is
                // the current page (max 50). Use pagination.totalItems
                // for the headline count so the user sees the full
                // catalogue size, not "50" on every page.
                values={{
                  count:
                    pagination?.totalItems != null ? pagination.totalItems : visibleListings.length,
                }}
              />
            )}
            {hasAnyFilter ? (
              <button
                type="button"
                className={css.clearFilterButton}
                onClick={() => {
                  setSelectedBrandSlug(null);
                  setSelectedFamily(null);
                  setSelectedCollection(null);
                }}
              >
                <FormattedMessage id="ProfilePage.clearFilters" defaultMessage="Clear filters" />
              </button>
            ) : null}
          </H4>
          <ul
            className={classNames(css.listings, {
              [css.listingsProjects]: isDesignProProfile,
            })}
          >
            {filteredListings.map(l => (
              <li className={css.listing} key={l.id.uuid}>
                {l?.attributes?.publicData?.listingType === 'design-showcase' ? (
                  <DesignListingCard
                    listing={l}
                    showAuthor={false}
                    sizes="(max-width: 1023px) 50vw, 33vw"
                  />
                ) : (
                  <ListingCard listing={l} showAuthorInfo={false} />
                )}
              </li>
            ))}
          </ul>
          {/* Paginate only when no pill is active. With a pill active the
              filter result is fetched as a single bag of up to 100 ids
              — there's no second page to walk to. */}
          {!hasAnyFilter && pagination && pagination.totalPages > 1 ? (
            <PaginationLinks
              className={css.pagination}
              pageName="ProfilePage"
              pagePathParams={{ id: user.id.uuid }}
              pagination={pagination}
            />
          ) : null}
          {filterTruncated ? (
            <p className={css.filterTruncatedNote}>
              <FormattedMessage
                id="ProfilePage.filterTruncated"
                defaultMessage="Showing first 100 of {total}. Clear the filter to browse the full catalogue."
                values={{ total: filterTotal }}
              />
            </p>
          ) : null}
        </div>
      ) : null}
      {hideReviews ? null : isMobileLayout ? (
        <MobileReviews
          reviews={reviews}
          queryReviewsError={queryReviewsError}
          userTypeRoles={userTypeRoles}
        />
      ) : (
        <DesktopReviews
          reviews={reviews}
          queryReviewsError={queryReviewsError}
          userTypeRoles={userTypeRoles}
        />
      )}
    </div>
  );
};

/**
 * ProfilePageComponent
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {boolean} props.useCurrentUser - Whether to use the current user
 * @param {propTypes.user|propTypes.currentUser} props.user - The user
 * @param {propTypes.error} props.userShowError - The user show error
 * @param {propTypes.error} props.queryListingsError - The query listings error
 * @param {Array<propTypes.listing|propTypes.ownListing>} props.listings - The listings
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.queryReviewsError - The query reviews error
 * @returns {JSX.Element} ProfilePageComponent
 */
export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    scrollingDisabled,
    params: pathParams,
    currentUser,
    useCurrentUser,
    userShowError,
    user,
    ...rest
  } = props;
  const isVariant = pathParams.variant?.length > 0;
  const isPreview = isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;

  // Stripe's onboarding needs a business URL for each seller, but the profile page can be
  // too empty for the provider at the time they are creating their first listing.
  // To remedy the situation, we redirect Stripe's crawler to the landing page of the marketplace.
  // TODO: When there's more content on the profile page, we should consider by-passing this redirection.
  const searchParams = rest?.location?.search;
  const isStorefront = searchParams
    ? new URLSearchParams(searchParams)?.get('mode') === 'storefront'
    : false;
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  const isCurrentUser = currentUser?.id && currentUser?.id?.uuid === pathParams.id;
  const profileUser = useCurrentUser ? currentUser : user;
  const { bio, displayName, publicData, metadata } = profileUser?.attributes?.profile || {};
  const { userFields } = config.user;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(userShowError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace = isPrivateMarketplace && hasNoViewingRightsUser;

  const userTypeRoles = getCurrentUserTypeRoles(config, profileUser);

  const isDataLoaded = isPreview
    ? currentUser != null || userShowError != null
    : hasNoViewingRightsOnPrivateMarketplace
    ? currentUser != null || userShowError != null
    : user != null || userShowError != null;

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  // Shared profile links used to preview with the marketplace-wide
  // "An online marketplace." blurb, which tells nobody which brand they
  // are about to open. Use the profile's own bio instead — <Page> feeds
  // this to <meta name="description">, og:description and
  // twitter:description, and falls back to the marketplace default when
  // a profile has no bio yet.
  const bioAsDescription = typeof bio === 'string' ? bio.replace(/\s+/g, ' ').trim() : '';
  const metaDescription =
    bioAsDescription.length > MAX_META_DESCRIPTION_LENGTH
      ? // Cut back to the last whole word so the ellipsis doesn't land
        // mid-word.
        `${bioAsDescription
          .slice(0, MAX_META_DESCRIPTION_LENGTH)
          .replace(/\s+\S*$/, '')
          .trim()}…`
      : bioAsDescription || null;

  if (!isDataLoaded) {
    return null;
  } else if (!isPreview && isNotFoundError(userShowError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(userShowError)
  ) {
    // Someone without viewing rights on a private marketplace is trying to
    // view a profile page that is not their own – redirect to NoAccessPage
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  } else if (!isPreview && isForbiddenError(userShowError)) {
    // This can happen if private marketplace mode is active, but it's not reflected through asset yet.
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  } else if (isPreview && mounted && !isCurrentUser) {
    // Someone is manipulating the URL, redirect to current user's profile page.
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={{ id: currentUser?.id?.uuid }} />
    ) : null;
  } else if ((isPreview || isPrivateMarketplace) && !mounted) {
    // This preview of the profile page is not rendered on server-side
    // and the first pass on client-side should render the same UI.
    return null;
  }

  // This is rendering normal profile page (not preview for pending-approval)
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      description={metaDescription}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        mainEntity: {
          '@type': 'Person',
          name: profileUser?.attributes?.profile?.displayName,
          ...(metaDescription ? { description: metaDescription } : {}),
        },
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn
        mainColumnClassName={css.profileMainColumn}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <MainContent
          user={profileUser}
          isOwn={mounted && isCurrentUser}
          bio={bio}
          displayName={displayName}
          userShowError={userShowError}
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFields}
          config={config}
          // Matterax: reviews feature disabled site-wide. Was previously
          // hideReviews={hasNoViewingRightsOnPrivateMarketplace} which
          // gated only on private-marketplace permissions. Forced true
          // suppresses both the desktop tabbed Reviews panel and the
          // mobile Reviews section unconditionally.
          hideReviews={true}
          intl={intl}
          userTypeRoles={userTypeRoles}
          {...rest}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    userListingsPagination,
    userCollectionIndex,
    reviews = [],
    queryReviewsError,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  // Show currentUser's data if it's not approved yet
  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    userShowError,
    queryListingsError,
    listings: getMarketplaceEntities(state, userListingRefs),
    pagination: userListingsPagination,
    collectionIndex: userCollectionIndex,
    reviews,
    queryReviewsError,
  };
};

const ProfilePage = compose(connect(mapStateToProps))(ProfilePageComponent);

export default ProfilePage;
